import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { productService } from '../services/productService';
import SafeIcon from '../common/SafeIcon';
import CopyButton from '../components/CopyButton';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiMapPin, FiTag, FiBox, FiCheckCircle, FiXCircle } = FiIcons;

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const data = await productService.getById(id);
        setProduct(data);
      } catch (err) { console.error(err); } finally { setLoading(false); }
    }
    load();
  }, [id]);

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-20 text-center"><div className="animate-spin inline-block w-8 h-8 border-2 border-gray-900 dark:border-white border-t-transparent rounded-full"></div></div>;
  if (!product) return <div className="max-w-7xl mx-auto px-6 py-20 text-center"><h2 className="text-xl font-medium text-gray-900 dark:text-white mb-2">Product Not Found</h2><Link to="/" className="text-sm text-blue-600 hover:underline">Return to Inventory</Link></div>;

  const getStatusColor = (status) => {
    if (status === 'In Stock') return 'text-emerald-700 bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/20';
    if (status === 'Low Stock') return 'text-amber-700 bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/20';
    return 'text-rose-700 bg-rose-50 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/20';
  };

  const MarketplaceBadge = ({ enabled, label }) => (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium ${enabled === 'TRUE' || enabled === '1' ? 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-100 dark:border-emerald-500/20 text-emerald-700 dark:text-emerald-400' : 'bg-gray-50 dark:bg-gray-800/50 border-gray-100 dark:border-gray-800 text-gray-400 dark:text-gray-600'}`}>
      <SafeIcon icon={enabled === 'TRUE' || enabled === '1' ? FiCheckCircle : FiXCircle} />
      {label}
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-8">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors mb-8">
        <SafeIcon icon={FiArrowLeft} /> Back to inventory
      </Link>
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden shadow-sm transition-colors">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="bg-gray-50 dark:bg-gray-800 aspect-square flex items-center justify-center p-8 border-r border-gray-100 dark:border-gray-800 transition-colors">
            <img src={product.image || 'https://via.placeholder.com/600x600?text=No+Image'} alt="" className="max-w-full max-h-full object-contain drop-shadow-xl" />
          </div>
          <div className="p-8 md:p-12 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border transition-colors ${getStatusColor(product.status)}`}>{product.status}</span>
              <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">Added: {product.CreationDate?.split('T')[0]}</span>
            </div>
            <h1 className="text-3xl font-light text-gray-900 dark:text-white mb-2 leading-tight transition-colors">{product.title}</h1>
            <div className="flex items-center gap-4 mb-8">
              <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded group/sku transition-colors">
                <SafeIcon icon={FiTag} className="text-[10px]" /> <span>{product.ProductID}</span> <CopyButton text={product.ProductID} />
              </div>
              {product.UPC && (
                <div className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500 font-mono group/upc">
                  UPC: {product.UPC} <CopyButton text={product.UPC} />
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-8 mb-10">
              <div><p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1">Site Price</p><p className="text-3xl font-medium text-gray-900 dark:text-white">${(product.price || 0).toFixed(2)}</p></div>
              <div><p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1">Stock</p><p className="text-3xl font-medium text-gray-900 dark:text-white">{product.stock} <span className="text-sm font-normal text-gray-400">pcs</span></p></div>
            </div>
            <div className="space-y-8">
              <div className="grid grid-cols-2 gap-6 py-6 border-y border-gray-100 dark:border-gray-800">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-50 dark:bg-blue-500/10 rounded-lg text-blue-600 dark:text-blue-400 transition-colors"><SafeIcon icon={FiMapPin} /></div>
                  <div>
                    <h3 className="text-xs font-semibold text-gray-900 dark:text-white uppercase">Location</h3>
                    <div className="flex items-center gap-1 group/loc"><p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{product.Location || 'Not Assigned'}</p>{product.Location && <CopyButton text={product.Location} />}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-50 dark:bg-purple-500/10 rounded-lg text-purple-600 dark:text-purple-400 transition-colors"><SafeIcon icon={FiBox} /></div>
                  <div><h3 className="text-xs font-semibold text-gray-900 dark:text-white uppercase">Category</h3><p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{product.Categories || 'Uncategorized'}</p></div>
                </div>
              </div>
              <div>
                <h3 className="text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">Marketplace Sync</h3>
                <div className="flex flex-wrap gap-2">
                  <MarketplaceBadge enabled={product.AmazonEnabled} label="Amazon" />
                  <MarketplaceBadge enabled={product.eBayEnabled} label="eBay" />
                  <MarketplaceBadge enabled={product.WebEnabled} label="WebStore" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;